import { useState, type ReactNode } from 'react';

import { api } from '../../api';
import {
  Aviso,
  Campo,
  Estado,
  Etiqueta,
  Painel,
  Tempo,
  data,
  sistemaDe,
  useAcao,
  useCarga,
} from '../../comum';
import type { Dispositivo } from '../../tipos';

/**
 * A ficha 360 do ativo e o que abre a partir dela.
 *
 * Saiu de `Inventario.tsx` por tamanho: a tela passava de 1.300 linhas e a
 * ficha respondia por um terço delas, sem compartilhar estado com a lista —
 * ela recebe um id e carrega sozinha.
 */
interface SoftwareInstalado {
  readonly nome: string;
  readonly fabricante: string | null;
  readonly versao: string | null;
  readonly categoria: string | null;
  readonly ehPatch: boolean;
  readonly instaladoEmBruto: string | null;
}

interface DiscoDaMaquina {
  readonly ordem: number;
  readonly nome: string;
  readonly tamanhoGb: number | null;
  readonly livreGb: number | null;
  readonly percentualUsado: number | null;
  readonly ehSistema: boolean;
}

interface DispositivoDetalhe extends Dispositivo {
  readonly camposCustomizados: readonly {
    readonly nome: string;
    readonly valor: string;
  }[];
  readonly rotulos?: string;
  readonly garantiaFim?: string | null;
  readonly garantiaNivel?: string | null;
  /** `null` = não foi possível carregar. `[]` = carregou e está vazio. */
  readonly softwares: readonly SoftwareInstalado[] | null;
  readonly discos: readonly DiscoDaMaquina[] | null;
  readonly viabilidade?: {
    readonly veredicto: { readonly kind: string };
    readonly resumo: string;
  };
  readonly biosNumeroSerie: string | null;
  readonly hwTpmVersao: string | null;
  readonly hwSecureBoot: string | null;
  readonly hwBitlocker: string | null;
  readonly soBuildBruto: string | null;
  readonly versaoAgente: string | null;
  readonly ultimoReboot: string | null;
}

/** Ficha 360 do ativo. */
export function Ficha({
  id,
  aoFechar,
  aoPerderSessao,
}: {
  id: string;
  aoFechar: () => void;
  aoPerderSessao: () => void;
}): ReactNode {
  const carga = useCarga<DispositivoDetalhe>(
    () => api.get(`/api/dispositivos/${encodeURIComponent(id)}`),
    [id],
    aoPerderSessao,
  );

  return (
    <div
      className="gaveta"
      onClick={(e) => {
        if (e.target === e.currentTarget) aoFechar();
      }}
    >
      <div>
        <div className="linha">
          <h2 style={{ margin: 0 }}>{carga.dados?.nome ?? 'Dispositivo'}</h2>
          <button className="encolhe" onClick={aoFechar}>
            Fechar
          </button>
        </div>

        <Estado carga={carga}>
          {carga.dados !== null && (
            <>
              {/*
                A IA cruzando os trinta campos da ficha.
                Sob demanda: o modelo roda em CPU dentro do cliente, e explicar
                toda máquina aberta gastaria minutos por uma leitura que nem
                sempre é pedida.
              */}
              <ExplicacaoDaMaquina id={id} aoPerderSessao={aoPerderSessao} />

              <table>
                <tbody>
                  <Linha r="Sistema" v={carga.dados.soNome} />
                  <Linha r="Build" v={carga.dados.soBuildBruto} />
                  <Linha
                    r="Hardware"
                    v={[carga.dados.hwFabricante, carga.dados.hwModelo].filter(Boolean).join(' ')}
                  />
                  <Linha r="Processador" v={carga.dados.hwCpuModelo} />
                  <Linha
                    r="Memória"
                    v={
                      carga.dados.hwRamMb === null
                        ? null
                        : `${(carga.dados.hwRamMb / 1024).toFixed(1)} GB`
                    }
                  />
                  <Linha r="Série" v={carga.dados.biosNumeroSerie} />
                  <Linha
                    r="Garantia"
                    v={
                      carga.dados.garantiaFim === undefined || carga.dados.garantiaFim === null
                        ? null
                        : `até ${data(carga.dados.garantiaFim)}` +
                          (carga.dados.garantiaNivel === null ||
                          carga.dados.garantiaNivel === undefined
                            ? ''
                            : ` · ${carga.dados.garantiaNivel}`)
                    }
                  />
                  <Linha r="TPM" v={carga.dados.hwTpmVersao} />
                  <Linha r="Secure Boot" v={carga.dados.hwSecureBoot} />
                  <Linha r="BitLocker" v={carga.dados.hwBitlocker} />
                  {/*
                    Os rótulos do KACE, sem os ocultos do appliance.
                    É por eles que o cliente já organiza o parque — e o que
                    permite escrever regra sem remodelar nada.
                  */}
                  <Linha
                    r="Rótulos"
                    v={
                      carga.dados.rotulos === undefined || carga.dados.rotulos === ''
                        ? null
                        : carga.dados.rotulos
                            .split('|')
                            .filter((x) => x !== '')
                            .join(' · ')
                    }
                  />
                  <Linha r="IP" v={carga.dados.ip} />
                  <Linha r="Usuário" v={carga.dados.usuarioLogado} />
                  <Linha r="Agente" v={carga.dados.versaoAgente} />
                  <Linha r="Último inventário" v={data(carga.dados.ultimoInventario)} />
                  <Linha r="Último reboot" v={data(carga.dados.ultimoReboot)} />
                </tbody>
              </table>

              {/*
                Discos.
                ⚠️ A associação `Machine_disks[].ID` do KACE repete o id da
                MÁQUINA, não do disco — por isso a chave aqui é a ordem.
              */}
              {(carga.dados.discos ?? []).length > 0 && (
                <>
                  <h3>Discos</h3>
                  <table>
                    <tbody>
                      {(carga.dados.discos ?? []).map((d) => (
                        <Linha
                          key={d.ordem}
                          r={`${d.nome}${d.ehSistema ? ' (sistema)' : ''}`}
                          v={
                            d.tamanhoGb === null
                              ? null
                              : `${d.livreGb ?? '?'} GB livres de ${d.tamanhoGb} GB` +
                                (d.percentualUsado === null ? '' : ` · ${d.percentualUsado}% usado`)
                          }
                        />
                      ))}
                    </tbody>
                  </table>
                </>
              )}

              {/*
                Software instalado.
                🔴 O produto sincronizava isto desde sempre e a ficha não
                mostrava nada — quem abria a máquina não conseguia responder
                "o que tem instalado aqui?".

                Patches ficam separados de propósito: numa máquina Windows eles
                são centenas e afogariam os programas que alguém procura.
              */}
              <h3>Software instalado</h3>
              <SoftwareDaMaquina itens={carga.dados.softwares} />

              <h3>Custom Inventory Rules</h3>
              {carga.dados.camposCustomizados.length === 0 ? (
                <p className="suave">
                  Nenhuma CIR coletada nesta máquina. É por CIR que chegam TPM, Secure Boot e espaço
                  em disco — sem elas, a análise de viabilidade fica indeterminada.
                </p>
              ) : (
                <table>
                  <tbody>
                    {carga.dados.camposCustomizados.map((c) => (
                      <Linha key={c.nome} r={c.nome} v={c.valor} />
                    ))}
                  </tbody>
                </table>
              )}
            </>
          )}
        </Estado>
      </div>
    </div>
  );
}

function Linha({ r, v }: { r: string; v: string | null | undefined }): ReactNode {
  return (
    <tr>
      <th style={{ width: 180, textTransform: 'none', letterSpacing: 0 }}>{r}</th>
      <td>{v === null || v === undefined || v === '' ? '—' : v}</td>
    </tr>
  );
}

/**
 * Lista de software da máquina.
 *
 * Patches escondidos por padrão: numa máquina Windows são centenas de
 * "Security Update for…" e eles afogariam os programas que alguém realmente
 * procura. O total continua visível, para ninguém achar que sumiu.
 */
function SoftwareDaMaquina({ itens }: { itens: readonly SoftwareInstalado[] | null }): ReactNode {
  const [filtro, setFiltro] = useState('');
  const [comPatches, setComPatches] = useState(false);

  /**
   * 🔴 "Não carregou" e "não tem" precisam de frases diferentes.
   *
   * A frase de baixo — *"máquina sem agente não reporta software"* — é uma
   * explicação tranquilizadora e correta **quando a consulta funcionou**. Se a
   * consulta falhou e mostrarmos ela mesmo assim, o produto explica com calma
   * um fato que ninguém verificou.
   */
  if (itens === null) {
    return (
      <p className="erro-cartao">
        Não foi possível carregar o software desta máquina. Isto é uma falha de consulta — não
        significa que a máquina não tenha software.
      </p>
    );
  }

  if (itens.length === 0) {
    return (
      <p className="suave">
        Nenhum software coletado. Máquina sem agente não reporta software — o dado não está faltando
        por erro.
      </p>
    );
  }

  const patches = itens.filter((s) => s.ehPatch).length;
  const busca = filtro.trim().toLowerCase();
  const lista = itens
    .filter((s) => (comPatches ? true : !s.ehPatch))
    .filter((s) => busca === '' || s.nome.toLowerCase().includes(busca))
    .sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR'));

  return (
    <>
      <div className="linha">
        <div>
          <input
            value={filtro}
            placeholder={`Filtrar entre ${itens.length - patches} programa(s)…`}
            onChange={(e) => setFiltro(e.target.value)}
          />
        </div>
        {patches > 0 && (
          <button className="encolhe" onClick={() => setComPatches((v) => !v)}>
            {comPatches ? `Esconder ${patches} patches` : `Mostrar ${patches} patches`}
          </button>
        )}
      </div>

      {lista.length === 0 ? (
        <p className="suave">Nenhum programa com esse nome nesta máquina.</p>
      ) : (
        <div className="rolagem lista-software">
          <table className="densa">
            <tbody>
              {lista.map((s, i) => (
                <tr key={`${s.nome}-${i}`}>
                  <td className="forte">{s.nome}</td>
                  <td className="suave">{s.versao ?? '—'}</td>
                  <td className="suave">{s.fabricante ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

/**
 * Abrir um chamado no KACE com as máquinas selecionadas.
 *
 * 🔴 **Esta é a única tela do produto que cria coisa no KACE por pedido direto
 * de uma pessoa.** Tudo mais que escreve nasce de uma regra aprovada.
 *
 * Por isso ela mostra exatamente o que vai acontecer antes de acontecer: quantas
 * máquinas, em que fila, com que título. O servidor ainda exige `confirmo: true`
 * — a trava vive lá porque uma tela pode ser trocada e um script pode chamar a
 * rota direto.
 *
 * **Um chamado, não N.** Vinte e uma máquinas viram uma tarefa com vinte e uma
 * linhas: o trabalho é um projeto, não vinte e um atendimentos. Abrir um por
 * máquina entupiria a fila do cliente no primeiro uso.
 */
export function AbrirChamadoEmLote({
  dispositivoIds,
  aoPerderSessao,
  aoFechar,
  aoAbrir,
}: {
  dispositivoIds: readonly string[];
  aoPerderSessao: () => void;
  aoFechar: () => void;
  aoAbrir: () => void;
}): ReactNode {
  const acao = useAcao(aoPerderSessao);
  const [titulo, setTitulo] = useState('');
  const [contexto, setContexto] = useState('');
  const [fila, setFila] = useState('');

  const filas = useCarga<{ itens: readonly { id: number; nome: string }[] }>(
    () =>
      api
        .get<{ itens: readonly { id: number; nome: string }[] }>('/api/integracoes/filas')
        .catch(() => ({
          itens: [] as readonly { id: number; nome: string }[],
        })),
    [],
    aoPerderSessao,
  );

  const enviar = (): void =>
    void acao.rodar(async () => {
      const r = await api.post<{
        idNoKace: number;
        maquinas: number;
        ignoradas: number;
      }>('/api/lote/abrir-chamado', {
        dispositivoIds,
        filaIdNoKace: Number(fila),
        titulo: titulo.trim(),
        contexto: contexto.trim(),
        // A confirmação é conferida no servidor; aqui ela é o registro de que
        // a pessoa viu o resumo antes de clicar.
        confirmo: true,
      });
      aoAbrir();
      return (
        `Chamado #${r.idNoKace} aberto no KACE com ${r.maquinas} máquina(s).` +
        (r.ignoradas > 0 ? ` ${r.ignoradas} não foram encontradas e ficaram de fora.` : '')
      );
    });

  return (
    <div className="editor-cartao">
      <header>
        <strong>Abrir chamado no KACE</strong>
        <button className="icone" onClick={aoFechar} aria-label="Fechar">
          ✕
        </button>
      </header>

      <Aviso tipo="alerta">
        Isto cria um chamado <strong>de verdade</strong> no Service Desk, com as{' '}
        {dispositivoIds.length} máquinas listadas no corpo. Um chamado só — não um por máquina.
      </Aviso>

      {acao.erro !== null && <Aviso tipo="erro">{acao.erro}</Aviso>}

      <Campo rotulo="Fila que vai receber">
        <select value={fila} onChange={(e) => setFila(e.target.value)}>
          <option value="">Escolha…</option>
          {(filas.dados?.itens ?? []).map((f) => (
            <option key={f.id} value={f.id}>
              {f.nome}
            </option>
          ))}
        </select>
      </Campo>

      <Campo rotulo="Título do chamado">
        <input
          value={titulo}
          maxLength={200}
          placeholder="Máquinas inaptas ao Windows 11 — avaliar troca"
          onChange={(e) => setTitulo(e.target.value)}
        />
      </Campo>

      <Campo
        rotulo="Contexto"
        ajuda="O que a pessoa que receber precisa saber. A lista de máquinas vai automaticamente."
      >
        <textarea
          value={contexto}
          rows={3}
          maxLength={1000}
          onChange={(e) => setContexto(e.target.value)}
        />
      </Campo>

      <div className="linha">
        <button
          className="primario encolhe"
          disabled={acao.executando || titulo.trim().length < 5 || fila === ''}
          onClick={enviar}
        >
          {acao.executando ? 'Abrindo…' : `Abrir chamado com ${dispositivoIds.length} máquina(s)`}
        </button>
        <button className="encolhe" onClick={aoFechar}>
          Cancelar
        </button>
        <span className="cresce" />
      </div>
    </div>
  );
}

/**
 * O que a IA conclui sobre esta máquina.
 *
 * A ficha mostra trinta campos; concluir a partir deles é trabalho que o
 * analista faz de cabeça, máquina por máquina. Aqui a IA faz o cruzamento —
 * sem dado novo, sem consulta, só leitura.
 *
 * O texto é conferido no servidor contra o nome da máquina: um parágrafo
 * confiante sobre o equipamento errado faria alguém agir no lugar errado.
 */
function ExplicacaoDaMaquina({
  id,
  aoPerderSessao,
}: {
  id: string;
  aoPerderSessao: () => void;
}): ReactNode {
  const [pedido, setPedido] = useState(false);

  const carga = useCarga<{
    readonly resumo: string;
    readonly pontos: readonly string[];
    readonly proximoPasso: string;
  }>(
    () =>
      pedido
        ? api.get(`/api/dispositivos/${encodeURIComponent(id)}/explicar`)
        : Promise.resolve({ resumo: '', pontos: [], proximoPasso: '' }),
    [pedido, id],
    aoPerderSessao,
  );

  if (!pedido) {
    return (
      <button className="encolhe" onClick={() => setPedido(true)}>
        ✦ O que isto significa?
      </button>
    );
  }

  return (
    <div className="explicacao-maquina">
      <Estado carga={carga}>
        {carga.dados !== null && carga.dados.resumo !== '' && (
          <>
            <p className="resumo-maquina">{carga.dados.resumo}</p>

            {carga.dados.pontos.length > 0 && (
              <ul className="pontos-maquina">
                {carga.dados.pontos.map((p, i) => (
                  <li key={i}>{p}</li>
                ))}
              </ul>
            )}

            {carga.dados.proximoPasso !== '' && (
              <p className="passo-maquina">
                <strong>Próximo passo:</strong> {carga.dados.proximoPasso}
              </p>
            )}
          </>
        )}
      </Estado>
    </div>
  );
}
